import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

export type DrillDown = {
  title: string;
  metric: string;
  timeRange: string;
  rule: string;
  deepLink?: string;
  breakdown?: { label: string; value: string }[];
};

export function InsightDrillDown({ trigger, data }: { trigger: React.ReactNode; data: DrillDown }) {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <button type="button" className="text-left w-full hover:bg-muted/40 rounded-md transition-colors cursor-pointer">
          {trigger}
        </button>
      </SheetTrigger>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{data.title}</SheetTitle>
          <SheetDescription>Underlying data & rule that generated this insight.</SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-5 text-sm">
          <Field label="Metric" value={data.metric} />
          <Field label="Time range" value={data.timeRange} />
          <Field label="Rule" value={data.rule} />
          {data.breakdown && data.breakdown.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Breakdown</p>
              <div className="rounded-md border divide-y">
                {data.breakdown.map((b, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2">
                    <span className="text-muted-foreground">{b.label}</span>
                    <span className="font-medium tabular-nums">{b.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {data.deepLink && (
            <Link to={data.deepLink}>
              <Button variant="outline" className="w-full">
                View full report <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{label}</p>
      <p className="leading-relaxed">{value}</p>
    </div>
  );
}
